/**
 * GA4-Checkout-/Purchase-Tracking-Helfer (Storage-Layer)
 *
 * Pure, try/catch-gekapselte Zugriffe auf session-/localStorage für die
 * GA4-`purchase`-Verdrahtung (architecture-ga4-funnel.md §7). Ausgelagert aus
 * `stripeApi`/`SubscriptionSuccessPage`, damit die Logik ohne DOM pur testbar ist.
 *
 * - PendingCheckoutRecord (sessionStorage, ein Slot): überlebt die Tab-Navigation
 *   zu Stripe und zurück und trägt `value`/`currency` an die Success-Seite, wo
 *   Stripe nur die `session_id` zurückgibt.
 * - Purchase-Dedup-Flag (localStorage, pro `session_id`): überlebt Reloads/Tab-
 *   Restore der Success-Seite und verhindert ein zweites `purchase`-Event.
 *
 * Alle Funktionen sind fail-safe: bei Storage-Exceptions (Edge-Umgebungen,
 * deaktiviertes Storage) wird geloggt und `undefined`/`false` zurückgegeben,
 * niemals geworfen — Tracking darf keinen Business-Flow brechen.
 */

import logger from './logger';

/** sessionStorage-Key des Pending-Checkout-Records (ein Slot, Overwrite bei Re-Checkout). */
const PENDING_CHECKOUT_KEY = 'faebill.ga4.pendingCheckout';

/** localStorage-Key-Präfix des Purchase-Dedup-Flags. */
const PURCHASE_FLAG_PREFIX = 'faebill.ga4.purchase.';

/** In sessionStorage abgelegter Checkout-Record. */
export interface PendingCheckoutRecord {
  priceId: string;
  value?: number;
  currency?: string;
  ts: number;
}

/**
 * Schreibt den Pending-Checkout-Record (überschreibt einen bestehenden Slot).
 * Wird in `startSubscriptionCheckout` nach Erhalt der Checkout-URL aufgerufen,
 * unmittelbar bevor der Caller zu Stripe weiterleitet.
 */
export function writePendingCheckout(record: Omit<PendingCheckoutRecord, 'ts'>): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }
    const payload: PendingCheckoutRecord = { ...record, ts: Date.now() };
    window.sessionStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(payload));
  } catch (error) {
    logger.error(error, 'writePendingCheckout failed');
  }
}

/**
 * Liest den Pending-Checkout-Record und löscht ihn (consume-once). Gibt
 * `undefined` zurück, wenn kein/ein invalider Record vorliegt.
 */
export function consumePendingCheckout(): PendingCheckoutRecord | undefined {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return undefined;
    }
    const raw = window.sessionStorage.getItem(PENDING_CHECKOUT_KEY);
    if (!raw) {
      return undefined;
    }
    // Erst entfernen (consume-once), dann parsen — auch bei kaputtem JSON weg.
    window.sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
    const parsed = JSON.parse(raw) as PendingCheckoutRecord;
    if (!parsed || typeof parsed.priceId !== 'string') {
      return undefined;
    }
    return parsed;
  } catch (error) {
    logger.error(error, 'consumePendingCheckout failed');
    return undefined;
  }
}

/**
 * Prüft, ob für diese `session_id` bereits ein `purchase`-Event gefeuert wurde
 * (Reload-Guard). Fail-safe `false` bei Storage-Fehlern.
 */
export function isPurchaseTracked(sessionId: string): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage || !sessionId) {
      return false;
    }
    return window.localStorage.getItem(PURCHASE_FLAG_PREFIX + sessionId) === '1';
  } catch (error) {
    logger.error(error, 'isPurchaseTracked failed');
    return false;
  }
}

/**
 * Markiert die `session_id` als getrackt (nach dem Feuern von `purchase`), damit
 * ein Reload derselben Success-URL kein zweites Event erzeugt.
 */
export function markPurchaseTracked(sessionId: string): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage || !sessionId) {
      return;
    }
    window.localStorage.setItem(PURCHASE_FLAG_PREFIX + sessionId, '1');
  } catch (error) {
    logger.error(error, 'markPurchaseTracked failed');
  }
}
